import React, { useEffect, useState } from 'react';

import { Alert, Button, Card, Empty, Input, InputNumber, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';

import { api } from '../../http/API';
import { Zond } from '../../models/Zond';

const { Text } = Typography;

const SOIL_TYPES = [
    { value: 0, label: 'Песок' },
    { value: 1, label: 'Супесь' },
    { value: 2, label: 'Глина' },
    { value: 3, label: 'Торф' },
];

// Тип 4 — Пост (4 датчика), остальные — зонд (3 датчика).
const isPostType = (zondTypeId?: number) => zondTypeId === 4;

interface Props {
    zonds: Zond[];
}

interface SoilForm {
    soil_t_1?: number;
    soil_t_2?: number;
    soil_t_3?: number;
    soil_t_4?: number;
    soil_depth_1_cm?: number;
    soil_depth_2_cm?: number;
    soil_depth_3_cm?: number;
    soil_depth_4_cm?: number;
}

export default function MyDevicesTab(props: Props) {
    const [editing, setEditing] = useState<Zond | null>(null);
    const [form, setForm] = useState<SoilForm>({});
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState<string>('');
    const [localZonds, setLocalZonds] = useState<Zond[]>(props.zonds || []);

    useEffect(() => {
        setLocalZonds(props.zonds || []);
    }, [props.zonds]);

    const openModal = (z: Zond) => {
        setEditing(z);
        setForm({
            soil_t_1: z.soil_t_1,
            soil_t_2: z.soil_t_2,
            soil_t_3: z.soil_t_3,
            soil_t_4: z.soil_t_4,
            soil_depth_1_cm: z.soil_depth_1_cm,
            soil_depth_2_cm: z.soil_depth_2_cm,
            soil_depth_3_cm: z.soil_depth_3_cm,
            soil_depth_4_cm: z.soil_depth_4_cm,
        });
    };

    const closeModal = () => {
        setEditing(null);
        setForm({});
    };

    const update = (patch: Partial<SoilForm>) => {
        setForm((prev) => ({ ...prev, ...patch }));
    };

    const save = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            await api.UpdateZondSoil(editing.id, form as any);
            message.success('Сохранено');
            // обновляем локально, не перезагружая страницу
            setLocalZonds((prev) =>
                prev.map((z) => (z.id === editing.id ? { ...z, ...form } : z)),
            );
            closeModal();
        } catch (e) {
            message.error('Не удалось сохранить');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: 'Устройство',
            dataIndex: 'number',
            key: 'number',
            render: (v: string, row: Zond) => `${row.zond_type?.displayed_name || 'Устройство'} №${v}`,
        },
        {
            title: 'Статус',
            dataIndex: ['zond_status', 'status'],
            key: 'status',
            render: (v: string, row: Zond) => {
                const isWorking = row.zond_status?.is_working;
                return <Tag color={isWorking ? 'green' : 'red'}>{v || 'Неизвестно'}</Tag>;
            },
        },
        {
            title: 'Место',
            dataIndex: 'place',
            key: 'place',
            render: (v: string) => v || '—',
        },
        {
            title: 'Почва',
            key: 'soil',
            render: (_: any, row: Zond) => {
                const hasAny =
                    row.soil_t_1 != null ||
                    row.soil_t_2 != null ||
                    row.soil_t_3 != null ||
                    row.soil_t_4 != null;
                const label = hasAny ? 'Настроена' : 'Не настроена';
                return (
                    <Space>
                        <Tag color={hasAny ? 'blue' : 'default'}>{label}</Tag>
                        <Button size="small" onClick={() => openModal(row)}>
                            Настроить
                        </Button>
                    </Space>
                );
            },
        },
    ];

    const count = editing ? (isPostType(editing.zond_type_id) ? 4 : 3) : 3;

    const filtered = localZonds.filter((z) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            (z.number || '').toLowerCase().includes(q) ||
            (z.number_in_db || '').toLowerCase().includes(q) ||
            (z.place || '').toLowerCase().includes(q) ||
            (z.description || '').toLowerCase().includes(q)
        );
    });

    if (!localZonds || localZonds.length === 0) {
        return (
            <Card>
                <Empty description="У вас пока нет привязанных устройств" />
            </Card>
        );
    }

    return (
        <>
            <Text type="secondary">
                Устройства привязаны к вашему кабинету. Управление привязкой — через администратора.
                Нажмите «Настроить», чтобы указать тип почвы и глубину датчиков.
            </Text>
            <div style={{ marginTop: 16, marginBottom: 8 }}>
                <Input.Search
                    placeholder="Поиск по номеру, месту или описанию"
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 400 }}
                />
                <Text type="secondary" style={{ marginLeft: 12 }}>
                    Показано: {filtered.length} из {localZonds.length}
                </Text>
            </div>
            <Table
                dataSource={filtered}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 20, showSizeChanger: false }}
                style={{ marginTop: 8 }}
            />

            <Modal
                title={editing ? `Настройка почвы: ${editing.zond_type?.displayed_name} №${editing.number}` : ''}
                open={!!editing}
                onCancel={closeModal}
                onOk={save}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={saving}
                width={600}
            >
                <div style={{ marginTop: 16 }}>
                    <Alert
                        type="info"
                        showIcon
                        message={<span style={{ fontSize: 13 }}>Как это работает</span>}
                        description={
                            <span style={{ fontSize: 12 }}>
                                Тип почвы влияет на расчёт продуктивной влажности.
                                Если известны глубины, на которых установлены датчики — укажите их.
                            </span>
                        }
                        style={{ marginBottom: 16 }}
                    />

                    <Text strong>Тип почвы по датчикам</Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 4, marginBottom: 8 }}>
                        Для каждого датчика укажите тип почвы на его глубине. Если тип отличается по слоям — выберите индивидуально.
                    </Text>
                    <div>
                        {Array.from({ length: count }, (_, i) => i + 1).map((n) => (
                            <Space key={n} style={{ display: 'flex', marginBottom: 8 }}>
                                <span style={{ width: 100, display: 'inline-block' }}>Датчик {n}:</span>
                                <Select
                                    style={{ width: 180 }}
                                    placeholder="Выберите тип"
                                    value={(form as any)[`soil_t_${n}`]}
                                    onChange={(v) => update({ [`soil_t_${n}`]: v } as any)}
                                    options={SOIL_TYPES}
                                    allowClear
                                />
                            </Space>
                        ))}
                    </div>

                    <Text strong style={{ display: 'block', marginTop: 16 }}>
                        Глубина датчиков (см)
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 4, marginBottom: 8 }}>
                        Опционально. Глубина в сантиметрах от поверхности почвы. Если не заполнено — используются значения по умолчанию (10/30/60 см для зондов, 10/30/50/80 см для постов).
                    </Text>
                    <div>
                        {Array.from({ length: count }, (_, i) => i + 1).map((n) => (
                            <Space key={n} style={{ display: 'flex', marginBottom: 8 }}>
                                <span style={{ width: 100, display: 'inline-block' }}>Датчик {n}:</span>
                                <InputNumber
                                    style={{ width: 180 }}
                                    placeholder="см"
                                    min={1}
                                    max={300}
                                    value={(form as any)[`soil_depth_${n}_cm`]}
                                    onChange={(v) => update({ [`soil_depth_${n}_cm`]: v as number } as any)}
                                />
                            </Space>
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    );
}
